import React from "react"
import { TextField } from "@mui/material"
import { useFormikContext } from "formik"
import i18n from "@/i18n"

export type ProfileFormValues = {
  firstName: string
  lastName: string
  email: string
  phone: string
  position: string
  country: string
  address: string
  city: string
  postcode: string
}

const STYLES = {
  section: "bg-white rounded-lg shadow p-6",
  fieldGrid: "grid grid-cols-1 md:grid-cols-2 gap-4 mb-4",
  fieldRow: "mb-4",
  addressTitle: "text-lg font-semibold mt-0 mb-4",
  addressGrid: "grid grid-cols-1 md:grid-cols-2 gap-4",
} as const

type PersonalInfoSectionProps = {
  isAdmin: boolean
  rolesDisplay: string
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({ isAdmin, rolesDisplay }) => {
  const formik = useFormikContext<ProfileFormValues>()

  return (
    <>
      <div className={STYLES.section} data-testid="settings-profile-section-personal">
        <div className={STYLES.fieldGrid}>
          <TextField
            label={i18n.t("app.settings.profile.overview.first_name")}
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={(formik.touched.firstName && formik.errors.firstName) as string}
            disabled={!isAdmin}
            fullWidth
            data-testid="settings-profile-input-first-name"
          />
          <TextField
            label={i18n.t("app.settings.profile.overview.last_name")}
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={(formik.touched.lastName && formik.errors.lastName) as string}
            disabled={!isAdmin}
            fullWidth
            data-testid="settings-profile-input-last-name"
          />
        </div>

        <div className={STYLES.fieldRow}>
          <TextField
            label={i18n.t("app.settings.profile.overview.email")}
            name="email"
            type="email"
            value={formik.values.email}
            disabled
            fullWidth
            data-testid="settings-profile-input-email"
          />
        </div>

        <div className={STYLES.fieldRow}>
          <TextField
            label={i18n.t("app.settings.profile.overview.phone")}
            name="phone"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={(formik.touched.phone && formik.errors.phone) as string}
            fullWidth
            data-testid="settings-profile-input-phone"
          />
        </div>

        <div className={STYLES.fieldRow}>
          <TextField
            label={i18n.t("app.settings.profile.overview.role")}
            value={rolesDisplay}
            disabled
            fullWidth
            data-testid="settings-profile-input-role"
          />
        </div>

        <div className={STYLES.fieldRow}>
          <TextField
            label={i18n.t("app.settings.profile.overview.position")}
            name="position"
            value={formik.values.position}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.position && Boolean(formik.errors.position)}
            helperText={(formik.touched.position && formik.errors.position) as string}
            fullWidth
            data-testid="settings-profile-input-position"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6" data-testid="settings-profile-section-address">
        <h3 className="text-lg font-semibold mt-0 mb-4">{i18n.t("app.settings.profile.overview.address_section")}</h3>

        <div className={STYLES.fieldRow}>
          <TextField
            label={i18n.t("app.settings.profile.overview.country")}
            name="country"
            value={formik.values.country}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.country && Boolean(formik.errors.country)}
            helperText={(formik.touched.country && formik.errors.country) as string}
            fullWidth
            data-testid="settings-profile-input-country"
          />
        </div>

        <div className={STYLES.fieldRow}>
          <TextField
            label={i18n.t("app.settings.profile.overview.address")}
            name="address"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address && Boolean(formik.errors.address)}
            helperText={(formik.touched.address && formik.errors.address) as string}
            fullWidth
            data-testid="settings-profile-input-address"
          />
        </div>

        <div className={STYLES.addressGrid}>
          <TextField
            label={i18n.t("app.settings.profile.overview.city")}
            name="city"
            value={formik.values.city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.city && Boolean(formik.errors.city)}
            helperText={(formik.touched.city && formik.errors.city) as string}
            fullWidth
            data-testid="settings-profile-input-city"
          />
          <TextField
            label={i18n.t("app.settings.profile.overview.postcode")}
            name="postcode"
            value={formik.values.postcode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.postcode && Boolean(formik.errors.postcode)}
            helperText={(formik.touched.postcode && formik.errors.postcode) as string}
            fullWidth
            data-testid="settings-profile-input-postcode"
          />
        </div>
      </div>
    </>
  )
}

export default PersonalInfoSection
